import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Seo, SITE } from "../components/Seo";
import { Footer } from "../components/Footer";
import { Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined=loading, null=not found
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setPost(undefined);
    axios.get(`${API}/posts/${slug}`).then(({ data }) => setPost(data)).catch(() => setPost(null));
    axios.get(`${API}/posts`).then(({ data }) => setRelated(data.filter((p) => p.slug !== slug))).catch(() => setRelated([]));
  }, [slug]);

  if (post === undefined) {
    return (
      <main className="max-w-3xl mx-auto px-5 py-20 min-h-[50vh] flex items-center gap-2 text-[#64748B]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </main>
    );
  }

  if (post === null) {
    return (
      <>
        <Seo title="Post not found | Nexus Numbers" description="This post could not be found." robots="noindex, nofollow" />
        <main className="max-w-3xl mx-auto px-5 py-20 min-h-[50vh]">
          <h1 className="font-display text-3xl font-bold text-[#0F172A]">Post not found</h1>
          <Link to="/blog" className="text-[#1E40AF] hover:underline mt-4 inline-block">Back to the blog</Link>
        </main>
        <Footer />
      </>
    );
  }

  const canonical = `${SITE}/blog/${post.slug}`;
  return (
    <>
      <Seo
        title={`${post.title} | Nexus Numbers`}
        description={post.metaDescription}
        canonicalPath={`/blog/${post.slug}`}
        ogType="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.metaDescription,
          datePublished: post.publishDate,
          dateModified: (post.updated_at || post.publishDate || "").slice(0, 10),
          mainEntityOfPage: canonical,
          author: { "@type": "Organization", name: "Nexus Numbers Paystub Generator" },
          publisher: { "@type": "Organization", name: "Nexus Numbers Paystub Generator" },
        }}
      />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14 min-h-[50vh]">
        <nav className="text-xs text-[#94A3B8] mb-6">
          <Link to="/" className="hover:text-[#1E40AF]">Home</Link> /{" "}
          <Link to="/blog" className="hover:text-[#1E40AF]">Blog</Link>
        </nav>
        <article>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0F172A]">{post.title}</h1>
          <div className="text-sm text-[#94A3B8] mt-2">{post.publishDate}</div>
          <div className="mt-8 text-[#334155] leading-relaxed">
            <ReactMarkdown
              components={{
                h1: ({ node, ...p }) => <h2 className="font-display text-2xl font-semibold text-[#0F172A] mt-8 mb-3" {...p} />,
                h2: ({ node, ...p }) => <h2 className="font-display text-xl font-semibold text-[#0F172A] mt-8 mb-3" {...p} />,
                h3: ({ node, ...p }) => <h3 className="font-display text-lg font-semibold text-[#0F172A] mt-6 mb-2" {...p} />,
                p: ({ node, ...p }) => <p className="my-4" {...p} />,
                ul: ({ node, ...p }) => <ul className="list-disc pl-6 my-4 space-y-1" {...p} />,
                ol: ({ node, ...p }) => <ol className="list-decimal pl-6 my-4 space-y-1" {...p} />,
                li: ({ node, ...p }) => <li className="pl-1" {...p} />,
                a: ({ node, ...p }) => <a className="text-[#1E40AF] underline" {...p} />,
              }}
            >
              {post.body}
            </ReactMarkdown>
          </div>
        </article>

        <div className="mt-12 rounded-2xl border border-[#D0E2FF] bg-[#F0F6FF] p-8">
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">Need a paystub?</h2>
          <p className="text-[#475569] mt-1">Create a professional earnings statement in minutes.</p>
          <Link to="/create" data-testid="post-generator-link"
            className="inline-flex items-center gap-1 mt-4 rounded-full bg-[#60A5FA] hover:bg-[#2563EB] text-white px-6 py-3 font-medium transition-colors">
            Create a paystub with Nexus Numbers <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {related.length > 0 && (
          <section className="mt-12" data-testid="related-articles">
            <h2 className="font-display text-xl font-semibold text-[#0F172A] mb-4">Related articles</h2>
            <ul className="space-y-2">
              {related.map((p) => (
                <li key={p.id}>
                  <Link to={`/blog/${p.slug}`} data-testid={`related-${p.slug}`} className="text-[#1E40AF] hover:underline">
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
};
